import {
  CheckCircleOutlineIcon,
  ClockOutlineIcon,
  type IconComponent,
  MinusCircleOutlineIcon,
  XCircleOutlineIcon,
} from '@fountain/ripple/icon';
import { MessageDescriptor } from 'react-intl';

import { LevelState } from './levelState';
import messages from './messages';

export type PillTone = 'approved' | 'rejected' | 'neutral' | 'warning';

export interface LevelPresentation {
  label: MessageDescriptor;
  tone: PillTone;
  Icon: IconComponent;
}

/**
 * How each level state is named and drawn.
 *
 * Shared for the same reason `levelState` is: the chain's level pill and the
 * approvals queue's status column (AXHE-3934) both say what a level has come
 * to, and a level that is "Cancelled" in one place and "Not triggered" in the
 * other is two answers to one question. The layouts differ — the rail lists
 * each approver, the cell has a row's worth of height — so the word, the tone
 * and the glyph are what is shared, not the markup.
 *
 * Colour is never the only cue: every entry carries a word, and the glyph is the
 * third. `notTriggered` and `cancelled` share a glyph deliberately — both are
 * "nobody was asked", and the words beside them carry the difference.
 */
export const LEVEL_PRESENTATION: Record<LevelState, LevelPresentation> = {
  approved: {
    label: messages.levelApproved,
    tone: 'approved',
    Icon: CheckCircleOutlineIcon,
  },
  rejected: {
    label: messages.levelRejected,
    tone: 'rejected',
    Icon: XCircleOutlineIcon,
  },
  active: {
    label: messages.levelActive,
    tone: 'neutral',
    Icon: ClockOutlineIcon,
  },
  notStarted: {
    label: messages.levelNotStarted,
    tone: 'neutral',
    Icon: ClockOutlineIcon,
  },
  notTriggered: {
    label: messages.levelNotTriggered,
    tone: 'neutral',
    Icon: MinusCircleOutlineIcon,
  },
  cancelled: {
    label: messages.levelCancelled,
    tone: 'neutral',
    Icon: MinusCircleOutlineIcon,
  },
};
